import React, { useState, useCallback, useEffect } from 'react';

import { Input, Button, UploadArea, Notification, Spinner } from '@ergeon/core-components';
import { PaperAirplaneIcon } from '@ergeon/icons/hero/micro';

import { UnifiedDialogEntry } from '../../../../../types';
import { formatPhoneNumber } from '../../../utils/formatUtils';
import { createOptimisticSMS } from '../../utils/createOptimisticEntry';

import { MAX_ATTACHMENTS, MAX_ATTACHMENT_SIZE } from './constants';
import { useCreateSMS } from './hooks/useCreateSMS';
import { useCallerId } from './hooks/useCallerId';
import { useComposeDraft, serializeAttachments, deserializeAttachments } from './hooks/useComposeDraft';
import { useFilePaste } from './hooks/useFilePaste';
import type { SMSDraft, SerializableAttachment } from './types';

const SMS_LABEL = 'Send SMS';
const SMS_PLACEHOLDER = 'Type your SMS message...';
const SMS_BUTTON_TEXT = 'Send SMS';
const YELP_LABEL = 'Send Yelp Message';
const YELP_PLACEHOLDER = 'Type your Yelp message...';
const YELP_BUTTON_TEXT = 'Send Yelp';
const BUTTON_LOADING_TEXT = 'Sending...';

type SMSInputProps = {
  customerId: number | undefined;
  customerName?: string;
  customerPhone?: string;
  initialText?: string | null;
  onSuccess?: () => void;
  toValidationError?: string | null;
  carrier?: 'sms' | 'yelp';
  yelpLeadKey?: string | null;
  temporaryPhone?: string | null;
  onAddOptimisticMessage?: (entry: UnifiedDialogEntry) => void;
  onUpdateOptimisticMessage?: (optimisticId: string, updates: Partial<UnifiedDialogEntry>) => void;
  onRemoveOptimisticMessage?: (optimisticId: string) => void;
};

const SMSInput: React.FC<SMSInputProps> = ({
  customerId,
  customerName,
  customerPhone,
  initialText,
  onSuccess,
  toValidationError,
  carrier = 'sms',
  yelpLeadKey,
  temporaryPhone,
  onAddOptimisticMessage,
  onUpdateOptimisticMessage: _onUpdateOptimisticMessage,
  onRemoveOptimisticMessage,
}) => {
  const isYelp = carrier === 'yelp';
  const [text, setText] = useState(initialText || '');
  const [attachments, setAttachments] = useState<Array<{dataURL?: string; file?: File}>>([]);
  const [draftAttachments, setDraftAttachments] = useState<SerializableAttachment[]>([]);

  const { sendSMS, sendYelpMessage, isSubmitting, error } = useCreateSMS();
  const { callerId, lineName, isLoading: isLoadingCallerId, error: callerIdError } = useCallerId(isYelp ? undefined : customerPhone);
  const { handlePaste } = useFilePaste({ attachments, setAttachments, maxUploads: MAX_ATTACHMENTS });

  const LABEL = isYelp ? YELP_LABEL : SMS_LABEL;
  const PLACEHOLDER = isYelp ? YELP_PLACEHOLDER : SMS_PLACEHOLDER;
  const BUTTON_TEXT = isYelp ? YELP_BUTTON_TEXT : SMS_BUTTON_TEXT;

  const getCurrentDraft = useCallback(
    (): Omit<SMSDraft, 'updatedAt'> => ({
      text,
      attachments: draftAttachments,
    }),
    [text, draftAttachments],
  );

  const handleDraftLoaded = useCallback(
    (draft: SMSDraft) => {
      if (!initialText) {
        setText(draft.text);
        setAttachments(deserializeAttachments(draft.attachments));
        setDraftAttachments(draft.attachments);
      }
    },
    [initialText],
  );

  const isDraftEmpty = useCallback(
    (draft: Omit<SMSDraft, 'updatedAt'>) => !draft.text.trim() && draft.attachments.length === 0,
    [],
  );

  const { saveDraft, clearDraft } = useComposeDraft<SMSDraft>({
    type: 'sms',
    customerId,
    getCurrentDraft,
    onDraftLoaded: handleDraftLoaded,
    isDraftEmpty,
    skipInitialLoad: !!initialText,
  });

  // Serialize attachments when they change
  useEffect(() => {
    serializeAttachments(attachments).then(setDraftAttachments);
  }, [attachments]);

  // Save draft when text or attachments change
  useEffect(() => {
    saveDraft();
  }, [text, draftAttachments, saveDraft]);

  // Sync text when initialText changes (e.g., URL navigation)
  useEffect(() => {
    if (initialText) {
      setText(initialText);
    }
  }, [initialText]);

  const hasContent = text.trim() || attachments.length > 0;
  const isDisabled = isYelp ?
    isSubmitting || !hasContent || !yelpLeadKey || !!toValidationError :
    isSubmitting || !hasContent || isLoadingCallerId || !callerId || !customerPhone || !!toValidationError;

  const handleSubmit = useCallback(async() => {
    if (!text.trim() && attachments.length === 0) return;

    // Create optimistic message
    let optimisticEntry: UnifiedDialogEntry | null = null;
    if (onAddOptimisticMessage) {
      const toPhone = isYelp ? (temporaryPhone || '') : (customerPhone || '');
      const fromPhone = callerId || '';
      // Remove "Call - " prefix from lineName if present
      const cleanLineName = lineName?.replace(/^Call - /, '');
      optimisticEntry = createOptimisticSMS({
        text,
        toPhone,
        fromPhone,
        publicPhoneLabel: cleanLineName || undefined,
        carrier: carrier === 'yelp' ? 'yelp' : null,
      });
      onAddOptimisticMessage(optimisticEntry);
    }

    let success = false;

    if (isYelp) {
      if (!yelpLeadKey) return;
      success = await sendYelpMessage({
        yelpLeadKey,
        message: text,
        attachments,
        toPhoneNumber: temporaryPhone || undefined,
      });
    } else {
      if (!customerId || !customerPhone) return;
      success = await sendSMS({
        customerId,
        toPhoneNumber: customerPhone,
        message: text,
        fromPhoneNumber: callerId || undefined,
        attachments,
      });
    }

    if (success) {
      setText('');
      setAttachments([]);
      clearDraft();
      // Call onSuccess first (triggers refetch)
      await onSuccess?.();
      // Remove optimistic message AFTER API data is loaded
      if (optimisticEntry && onRemoveOptimisticMessage) {
        onRemoveOptimisticMessage(optimisticEntry.optimisticId!);
      }
    } else if (optimisticEntry && onRemoveOptimisticMessage) {
      // Remove optimistic message on failure
      onRemoveOptimisticMessage(optimisticEntry.optimisticId!);
    }
  }, [
    isYelp,
    yelpLeadKey,
    customerId,
    text,
    customerPhone,
    callerId,
    lineName,
    carrier,
    attachments,
    temporaryPhone,
    onSuccess,
    sendSMS,
    sendYelpMessage,
    clearDraft,
    onAddOptimisticMessage,
    onRemoveOptimisticMessage,
  ]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Cmd + Enter (Mac) or Ctrl + Enter (Windows/Linux) to send
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const renderNotificationText = () => {
    if (isYelp) {
      return `You are about to send a Yelp message to ${customerName}.`;
    }

    const formattedCustomerPhone = customerPhone ? formatPhoneNumber(customerPhone) : null;
    const formattedCallerId = callerId ? formatPhoneNumber(callerId) : null;

    const recipient = formattedCustomerPhone ? `${customerName} at ${formattedCustomerPhone}` : customerName;
    const baseText = `You are about to send SMS to ${recipient}`;

    if (isLoadingCallerId) {
      return `${baseText}. Loading caller ID...`;
    }
    if (formattedCallerId) {
      if (lineName) {
        // Remove "Call - " prefix from lineName if present
        const cleanLineName = lineName.replace(/^Call - /, '');
        return `${baseText} from ${cleanLineName} - ${formattedCallerId}.`;
      }
      return `${baseText} from ${formattedCallerId}.`;
    }
    return `${baseText}.`;
  };

  return (
    <div className="flex flex-col gap-3 w-full shrink-0">
      {toValidationError && (
        <Notification mode="embed" type="Error">
          {toValidationError}
        </Notification>
      )}

      {customerName && !toValidationError && (
        <Notification mode="embed" size="small" type="Information">
          {renderNotificationText()}
        </Notification>
      )}

      {(error || (!isYelp && callerIdError)) && (
        <Notification mode="embed" type="Error">
          {error || callerIdError}
        </Notification>
      )}

      <Input
        className="flex-1 [&_textarea]:scrollbar-hide [&_textarea]:[scrollbar-width:none] [&_textarea]:[&::-webkit-scrollbar]:[display:none]"
        disabled={isSubmitting}
        isMultiline
        label={LABEL}
        multilineRows={3}
        onChange={(_event, _name, value) => setText(value || '')}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={PLACEHOLDER}
        value={text} />

      <div className="flex lg:flex-row flex-col gap-4">
        <UploadArea filesList={attachments} maxFileSize={MAX_ATTACHMENT_SIZE} maxUploads={MAX_ATTACHMENTS} onChange={setAttachments} />
        <Button
          className="lg:max-w-36" disabled={isDisabled} flavor="primary" isFullWidth
          onClick={handleSubmit}>
          {isSubmitting ? (
            <>
              <Spinner active color="white" size={18} />
              {BUTTON_LOADING_TEXT}
            </>
          ) : (
            <>
              <PaperAirplaneIcon className="size-5 -rotate-45 shrink-0" />
              {BUTTON_TEXT}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default SMSInput;
